import has from 'lodash/has';
import isNil from 'lodash/isNil';

import { ROUTE_OAUTH2_AUTH } from '../constants/pathnames';
import { getCurrentUtcTime } from '../lib/DateUtils';
import UIUtils from '../lib/UIUtils';
import { getCorsCompatibleURL } from '../lib/URLUtils';
import { generateCodeVerifier, generateCodeChallenge } from './utils/oidc';

const AUTH_URL_PARAMS_FIELD = 'oauth2.auth_url.auth_url_params';
const TOKEN_URL_PARAMS_FIELD = 'oauth2.token_url.token_url_params';
const TOKEN_URL_BASIC_AUTH_HEADER_FIELD = 'oauth2.basic_auth';
const PKCE_ENABLED_FIELD = 'oauth2.pkce_enabled';
const PKCE_SEND_CLIENT_SECRET_FIELD = 'oauth2.pkce_include_client_secret';

const REDIRECT_URI = window.location.protocol + '//' + window.location.host + ROUTE_OAUTH2_AUTH;

function authenticate(fn, credInfo) {
  const clientId = credInfo['oauth2.client.id'];
  const clientSecret = credInfo['oauth2.client.secret'];
  const pkceEnabled =
    credInfo[PKCE_ENABLED_FIELD] === 'true' || credInfo[PKCE_ENABLED_FIELD] === true;
  const pkceSendClientSecret =
    credInfo[PKCE_SEND_CLIENT_SECRET_FIELD] === 'true' ||
    credInfo[PKCE_SEND_CLIENT_SECRET_FIELD] === true;

  let codeVerifier = null;
  if (pkceEnabled) {
    codeVerifier = generateCodeVerifier();
  }

  window.HandlePopupResult = function (authResponse, error) {
    delete window.HandlePopupResult;

    const codeProperty = credInfo['oauth2.auth_response.code'] || 'code';
    let accessError = null;

    if (error) {
      accessError = error;
    } else if (has(authResponse, 'error')) {
      accessError = {
        code: authResponse.error,
        description: has(authResponse, 'error_description')
          ? authResponse.error_description
          : authResponse.error,
      };
    } else if (has(authResponse, 'access_token')) {
      fn({ ...authResponse, authorizedAt: getCurrentUtcTime() });
    } else if (has(authResponse, codeProperty)) {
      const credTokenUrl = !isNil(credInfo['oauth2.access.token.url'])
        ? credInfo['oauth2.access.token.url']
        : '';
      const tokenURL = getCorsCompatibleURL(credTokenUrl);

      let tokenParams = [
        `redirect_uri=${encodeURIComponent(REDIRECT_URI)}`,
        `code=${authResponse[codeProperty]}`,
        `client_id=${clientId}`,
        'grant_type=authorization_code',
      ];

      if (pkceEnabled && codeVerifier) {
        tokenParams.push(`code_verifier=${codeVerifier}`);
        if (pkceSendClientSecret) {
          tokenParams.push(`client_secret=${clientSecret}`);
        }
      } else {
        tokenParams.push(`client_secret=${clientSecret}`);
      }

      const extraTokenUrlParams = credInfo[TOKEN_URL_PARAMS_FIELD];

      if (!isNil(extraTokenUrlParams)) {
        tokenParams.push(extraTokenUrlParams);
      }
      tokenParams = tokenParams.join('&');
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        Accept: 'application/json',
      };

      if (!isNil(credInfo[TOKEN_URL_BASIC_AUTH_HEADER_FIELD])) {
        headers.Authorization = `Basic ${credInfo[TOKEN_URL_BASIC_AUTH_HEADER_FIELD]}`;
      }

      fetch(tokenURL, {
        method: credInfo['oauth2.access.token.method'],
        body: tokenParams,
        headers,
      })
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            return response.json();
          }
          throw new Error(response.statusText);
        })
        .then((tokenResponse) => {
          fn({
            ...authResponse,
            ...tokenResponse,
            authorizedAt: getCurrentUtcTime(),
          });
        })
        .catch(console.log);
    }

    if (accessError) {
      fn(null, accessError);
    }
  };

  const authURL = credInfo['oauth2.auth.url'];
  const authUrlParams = credInfo[AUTH_URL_PARAMS_FIELD];
  const state = UIUtils.generateNonce();
  const authParams = [
    `redirect_uri=${encodeURIComponent(REDIRECT_URI)}`,
    'response_type=code',
    `state=${state}`,
    `client_id=${clientId}`,
  ];

  if (pkceEnabled && codeVerifier) {
    const codeChallenge = generateCodeChallenge(codeVerifier);
    authParams.push(`code_challenge=${codeChallenge}`);
    authParams.push('code_challenge_method=S256');
  }

  if (!isNil(authUrlParams)) {
    authParams.push(`${authUrlParams}`);
  }

  window.open(
    authURL + (authURL.includes('?') ? '&' : '?') + authParams.join('&'),
    'Authorization',
    'top=300,left=250,height=600,width=600',
  );
}

export default { authenticate };
